import React, { useState, useEffect, useCallback } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import Layout from '../components/Layout';
import { crmAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const fmtDate = d => formatDateDMY(d, '—');
const fmtNum = n => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const CRM = () => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const pg = { color: darkMode ? '#E2E8F0' : '#1E293B' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const card = { background: darkMode ? '#1E293B' : '#FFFFFF', borderRadius: 12, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, padding: 20 };
  const inp = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#E2E8F0' : '#1E293B', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const btnPrimary = { padding: '8px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
  const btnSecondary = { padding: '8px 18px', background: darkMode ? '#334155' : '#E2E8F0', color: darkMode ? '#CBD5E1' : '#64748B', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
  const lbl = { ...muted, fontSize: 11, fontWeight: 600, marginBottom: 4, display: 'block' };

  const [tab, setTab] = useState('pipeline');
  const [leads, setLeads] = useState([]);
  const [pipelineStats, setPipelineStats] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [segments, setSegments] = useState(null);
  const [loyaltyPrograms, setLoyaltyPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);

  const [leadForm, setLeadForm] = useState({ name: '', phone: '', email: '', source: 'walk-in', expected_value: 0, notes: '' });
  const [fuForm, setFuForm] = useState({ lead_id: '', follow_up_date: '', follow_up_time: '', notes: '' });

  const stageColors = { new: '#3B82F6', contacted: '#8B5CF6', qualified: '#F59E0B', proposal: '#F97316', won: '#10B981', lost: '#EF4444' };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'pipeline') {
        const [lRes, pRes] = await Promise.all([crmAPI.listLeads(), crmAPI.pipelineStats()]);
        setLeads(Array.isArray(lRes.data?.items) ? lRes.data.items : []);
        setPipelineStats(pRes.data);
      } else if (tab === 'followups') {
        const r = await crmAPI.listFollowUps();
        setFollowUps(Array.isArray(r.data) ? r.data : []);
      } else if (tab === 'segments') {
        const r = await crmAPI.customerSegments();
        setSegments(r.data);
      } else if (tab === 'loyalty') {
        const r = await crmAPI.listLoyaltyPrograms();
        setLoyaltyPrograms(Array.isArray(r.data) ? r.data : []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [tab]);

  useEffect(() => { loadData(); }, [loadData]);

  const createLead = async (e) => {
    e.preventDefault();
    try { await crmAPI.createLead(leadForm); setShowLeadForm(false); setLeadForm({ name: '', phone: '', email: '', source: 'walk-in', expected_value: 0, notes: '' }); loadData(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const convertLead = async (id) => {
    try { await crmAPI.convertLead(id); loadData(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const createFollowUp = async (e) => {
    e.preventDefault();
    try {
      await crmAPI.createFollowUp({
        entity_type: 'lead',
        entity_id: fuForm.lead_id,
        follow_up_date: fuForm.follow_up_date,
        follow_up_time: fuForm.follow_up_time || null,
        description: fuForm.notes,
      });
      setShowFollowUpForm(false);
      setFuForm({ lead_id: '', follow_up_date: '', follow_up_time: '', notes: '' });
      loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const tabs = [
    { key: 'pipeline', label: '🎯 Pipeline' },
    { key: 'followups', label: '📞 Follow-ups' },
    { key: 'segments', label: '👥 Segments' },
    { key: 'loyalty', label: '⭐ Loyalty' },
  ];

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <h1 style={{ ...pg, fontSize: 22, fontWeight: 800, marginBottom: 20 }}>🎯 CRM & Leads</h1>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${tab === t.key ? acc : (darkMode ? '#334155' : '#E2E8F0')}`, background: tab === t.key ? acc : 'transparent', color: tab === t.key ? '#fff' : pg.color, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div style={{ ...muted, textAlign: 'center', padding: 40 }}>Loading...</div>}

        {/* Pipeline */}
        {tab === 'pipeline' && !loading && (
          <div>
            {/* Stats */}
            {pipelineStats && (
              <div className="stagger-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
                {Object.entries(pipelineStats.pipeline || {}).map(([stage, data]) => (
                  <div key={stage} style={{ ...card, borderTop: `4px solid ${stageColors[stage] || acc}` }}>
                    <div style={{ ...muted, fontSize: 11, textTransform: 'uppercase' }}>{stage}</div>
                    <div style={{ ...pg, fontSize: 22, fontWeight: 800 }}>{data.count || 0}</div>
                    <div style={{ ...muted, fontSize: 11 }}>₹{fmtNum(data.value)}</div>
                  </div>
                ))}
              </div>
            )}

            <button onClick={() => setShowLeadForm(!showLeadForm)} style={{ ...btnPrimary, marginBottom: 16 }}>{showLeadForm ? 'Cancel' : '+ New Lead'}</button>

            {showLeadForm && (
              <form onSubmit={createLead} style={{ ...card, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
                  <div><label style={lbl}>Name</label><input value={leadForm.name} onChange={e => setLeadForm(f => ({ ...f, name: e.target.value }))} style={inp} required /></div>
                  <div><label style={lbl}>Phone</label><input value={leadForm.phone} onChange={e => setLeadForm(f => ({ ...f, phone: e.target.value }))} style={inp} /></div>
                  <div><label style={lbl}>Email</label><input value={leadForm.email} onChange={e => setLeadForm(f => ({ ...f, email: e.target.value }))} style={inp} /></div>
                  <div><label style={lbl}>Source</label>
                    <select value={leadForm.source} onChange={e => setLeadForm(f => ({ ...f, source: e.target.value }))} style={inp}>
                      {['walk-in', 'phone', 'website', 'referral', 'social-media', 'advertisement', 'other'].map(s => <option key={s} value={s}>{s.replace(/-/g, ' ')}</option>)}
                    </select>
                  </div>
                  <div><label style={lbl}>Expected Value (₹)</label><input type="number" value={leadForm.expected_value} onChange={e => setLeadForm(f => ({ ...f, expected_value: Number(e.target.value) }))} style={inp} /></div>
                  <div><label style={lbl}>Notes</label><input value={leadForm.notes} onChange={e => setLeadForm(f => ({ ...f, notes: e.target.value }))} style={inp} /></div>
                </div>
                <button type="submit" style={btnPrimary}>Create Lead</button>
              </form>
            )}

            {/* Leads List */}
            {leads.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', ...muted, padding: 40 }}>No leads yet. Start adding leads to track your sales pipeline.</div>
            ) : (
              <div style={card}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                      {['Name', 'Phone', 'Source', 'Status', 'Value', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', ...muted, fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map(lead => (
                      <tr key={lead.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ padding: '8px 10px', ...pg, fontWeight: 600 }}>{lead.name}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{lead.phone || '—'}</td>
                        <td style={{ padding: '8px 10px', ...pg, textTransform: 'capitalize' }}>{lead.source?.replace(/_/g, ' ')}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: `${stageColors[lead.status] || acc}22`, color: stageColors[lead.status] || acc }}>
                            {lead.status?.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px', ...pg, fontWeight: 600 }}>₹{fmtNum(lead.expected_value)}</td>
                        <td style={{ padding: '8px 10px', display: 'flex', gap: 6 }}>
                          {lead.status !== 'won' && lead.status !== 'lost' && (
                            <button onClick={() => convertLead(lead.id)} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 11 }}>Convert</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Follow-ups */}
        {tab === 'followups' && !loading && (
          <div>
            <button onClick={() => setShowFollowUpForm(!showFollowUpForm)} style={{ ...btnPrimary, marginBottom: 16 }}>{showFollowUpForm ? 'Cancel' : '+ Schedule Follow-up'}</button>

            {showFollowUpForm && (
              <form onSubmit={createFollowUp} style={{ ...card, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  <div><label style={lbl}>Lead</label>
                    <select value={fuForm.lead_id} onChange={e => setFuForm(f => ({ ...f, lead_id: e.target.value }))} style={inp} required>
                      <option value="">Select Lead</option>
                      {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                  <div><label style={lbl}>Date</label><StrictDateInput value={fuForm.follow_up_date} onChange={e => setFuForm(f => ({ ...f, follow_up_date: e.target.value }))} style={inp} required /></div>
                  <div><label style={lbl}>Type</label>
                    <input type="time" value={fuForm.follow_up_time} onChange={e => setFuForm(f => ({ ...f, follow_up_time: e.target.value }))} style={inp} />
                  </div>
                  <div><label style={lbl}>Notes</label><input value={fuForm.notes} onChange={e => setFuForm(f => ({ ...f, notes: e.target.value }))} style={inp} /></div>
                </div>
                <button type="submit" style={{ ...btnPrimary, marginTop: 12 }}>Schedule</button>
              </form>
            )}

            {followUps.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', ...muted, padding: 40 }}>No follow-ups scheduled</div>
            ) : (
              <div style={card}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                      {['Date', 'Type', 'Lead', 'Notes', 'Status'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', ...muted, fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {followUps.map(fu => (
                      <tr key={fu.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ padding: '8px 10px', ...pg }}>{fmtDate(fu.follow_up_date)}</td>
                        <td style={{ padding: '8px 10px', ...pg, textTransform: 'capitalize' }}>{fu.entity_type}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{fu.entity_id}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{fu.description || '—'}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: fu.status === 'completed' ? '#DCFCE7' : '#FEF3C7', color: fu.status === 'completed' ? '#166534' : '#92400E' }}>
                            {fu.status === 'completed' ? 'Done' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Customer Segments */}
        {tab === 'segments' && !loading && segments && (
          <div className="stagger-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            {Object.entries(segments.segments || {}).map(([seg, data]) => {
              const segColors = { vip: '#F59E0B', regular: '#3B82F6', occasional: '#8B5CF6', inactive: '#EF4444', new: '#10B981' };
              return (
                <div key={seg} style={{ ...card, borderTop: `4px solid ${segColors[seg] || acc}` }}>
                  <div style={{ ...pg, fontSize: 16, fontWeight: 800, textTransform: 'capitalize', marginBottom: 8 }}>{seg}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div><div style={muted}>Customers</div><div style={{ ...pg, fontSize: 20, fontWeight: 800 }}>{data.count}</div></div>
                    <div><div style={muted}>Top Value</div><div style={{ ...pg, fontSize: 16, fontWeight: 700 }}>₹{fmtNum(data.customers?.[0]?.total_purchases || 0)}</div></div>
                  </div>
                  {data.customers?.slice(0, 5).map((c, i) => (
                    <div key={i} style={{ padding: '4px 0', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={pg}>{c.name}</span><span style={muted}>₹{fmtNum(c.total_purchases || c.total_revenue)}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Loyalty Programs */}
        {tab === 'loyalty' && !loading && (
          <div>
            {loyaltyPrograms.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', ...muted, padding: 40 }}>No loyalty programs configured yet</div>
            ) : (
              <div className="stagger-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                {loyaltyPrograms.map(lp => (
                  <div key={lp.id} style={{ ...card, borderLeft: `4px solid ${acc}` }}>
                    <div style={{ ...pg, fontSize: 16, fontWeight: 700 }}>{lp.name}</div>
                    <div style={muted}>Points: {fmtNum(lp.points_per_amount)} / ₹{fmtNum(lp.amount_unit)} · Redeem: ₹{fmtNum(lp.redemption_rate)} / point</div>
                    <div style={{ ...muted, fontSize: 11, marginTop: 4 }}>Min: {lp.min_points_redeem} pts · {lp.is_active ? '🟢 Active' : '🔴 Inactive'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CRM;
